<template>
	<div class="font-mono text-sm">
		<template v-if="isExpandable">
			<button
				class="flex w-full items-center gap-1 rounded px-1.5 py-1 hover:bg-orange-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-600 dark:hover:bg-orange-900"
				@click.stop="expanded = !expanded"
			>
				<div class="grid h-4 w-4 place-items-center text-gray-600 dark:text-gray-400">
					<FAIcon v-if="!expanded" icon="chevron-right" />
					<FAIcon v-else class="mb-0.5" icon="chevron-down" />
				</div>
				<div>
					<span v-if="keyName" class="font-semibold text-orange-600">
						{{ keyName }}:
					</span>
					<span class="capitalize text-gray-600 dark:text-gray-400">
						{{ formatValue() }}
					</span>
				</div>
			</button>

			<template v-if="expanded">
				<div class="ml-[13px] border-l border-gray-300 pl-2 dark:border-gray-700">
					<div v-for="(item, index) in data" :key="index">
						<JsonNode :data="item" :key-name="index.toString()" />
					</div>
				</div>
				<span class="ml-2.5 text-gray-600 dark:text-gray-400">{{
					isArray ? ']' : '}'
				}}</span>
			</template>
		</template>

		<div v-else class="py-1 pl-6">
			<span v-if="keyName" class="font-semibold text-orange-600">{{ keyName }}: </span>
			<component
				:is="type === 'url' ? 'a' : 'span'"
				:href="type === 'url' ? props.data : undefined"
				:target="type === 'url' ? '_blank' : undefined"
				:class="
					['string', 'url'].includes(type)
						? '!inline !font-normal !text-green-700 dark:!text-green-400'
						: 'text-blue-500 dark:text-blue-300'
				"
			>
				{{ formatValue() }}
			</component>
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
	data: {
		type: [Object, Array, String, Number, Boolean, null],
		required: true,
	},
	keyName: {
		type: String,
		default: '',
	},
	isRoot: {
		type: Boolean,
		default: false,
	},
});

const expanded = ref(props.isRoot && props.data && Object.keys(props.data).length > 0);

const isArray = computed(() => Array.isArray(props.data));
const isObject = computed(
	() => props.data !== null && typeof props.data === 'object' && !isArray.value
);
const isExpandable = computed(() => isArray.value || isObject.value);
const type = computed(() => {
	const type = typeof props.data;
	if (type === 'object' && isArray.value) {
		return 'array';
	}
	if (type === 'string' && isValidUrl(props.data)) {
		return 'url';
	}
	return type;
});

function isValidUrl(string) {
	try {
		new URL(string);
		return true;
	} catch {
		return false;
	}
}

function formatValue() {
	if (props.data === null) return 'null';
	if (props.data === undefined) return 'undefined';

	switch (type.value) {
		case 'string':
		case 'url':
			return `"${props.data}"`;
		case 'number':
		case 'boolean':
			return props.data.toString();
		default:
			return `${type.value}${isArray.value ? '[' : '{'}${Object.keys(props.data).length}${isArray.value ? ']' : '}'}`;
	}
}
</script>
